import { useCallback, useRef, Dispatch, SetStateAction, CSSProperties } from 'react';
// Fix: Use named imports for ReactFlow functions and types from reactflow.
import { addEdge, Connection, Edge, OnConnectStartParams } from 'reactflow';
import { AppNode } from '../types';
import { DEFAULT_EDGE_STROKE } from '../utils/layoutConstants';

export const useEdgeOperations = (
    nodes: AppNode[],
    setNodes: Dispatch<SetStateAction<AppNode[]>>,
    setEdges: Dispatch<SetStateAction<Edge[]>>
) => {
    const connectingNodeId = useRef<string | null>(null);
    const connectingHandleId = useRef<string | null>(null);
    // React Flow calls onConnectEnd after every connection gesture, including
    // gestures that already completed through onConnect. Track that outcome so
    // the drop-on-node fallback cannot create a second edge for the same gesture.
    const connectionCompleted = useRef(false);

    const hasEquivalentEdge = useCallback((
        edges: Edge[],
        connection: Pick<Connection, 'source' | 'target' | 'sourceHandle' | 'targetHandle'>,
    ): boolean => edges.some(edge =>
        edge.source === connection.source &&
        edge.target === connection.target &&
        (edge.sourceHandle ?? null) === (connection.sourceHandle ?? null) &&
        (edge.targetHandle ?? null) === (connection.targetHandle ?? null)
    ), []);

    // Resolve edge label from gateway outputs when sourceHandle maps to an output index
    const resolveGatewayLabel = useCallback((sourceId: string | null, sourceHandle: string | null): string => {
        if (!sourceId || !sourceHandle) return '';
        const sourceNode = nodes.find(n => n.id === sourceId);
        if (!sourceNode) return '';
        const outputs = sourceNode.data?.outputs;
        if (!Array.isArray(outputs)) return '';
        const handleIdx = parseInt(sourceHandle, 10);
        if (isNaN(handleIdx) || handleIdx < 0 || handleIdx >= outputs.length) return '';
        return outputs[handleIdx] || '';
    }, [nodes]);

    const onConnect = useCallback(
        (params: Connection) => {
            connectionCompleted.current = true;
            setEdges((eds) => {
                if (hasEquivalentEdge(eds, params)) return eds;

                const label = resolveGatewayLabel(params.source, params.sourceHandle);
                // Boundary associations should be solid and non-animated; everything else animates.
                const animated = params.sourceHandle !== 'boundary';
                const style: CSSProperties = { stroke: DEFAULT_EDGE_STROKE };

                return addEdge({
                    ...params,
                    type: 'deletable',
                    animated,
                    label,
                    style,
                }, eds);
            });
        },
        [setEdges, resolveGatewayLabel, hasEquivalentEdge]
    );

    const onConnectStart = useCallback((_: any, { nodeId, handleId }: OnConnectStartParams) => {
        connectingNodeId.current = nodeId;
        connectingHandleId.current = handleId;
        connectionCompleted.current = false;

        setNodes((nds) => nds.map((node) => {
            if (node.id === nodeId) return node;
            return {
                ...node,
                data: {
                    ...node.data,
                    isConnectableTarget: true
                }
            };
        }));
    }, [setNodes]);

    const onConnectEnd = useCallback((event: any) => {
        const target = event.target as HTMLElement;
        const targetNodeElement = target.closest('.react-flow__node') as HTMLElement;
        const isHandle = target.classList.contains('react-flow__handle');

        if (!connectionCompleted.current && targetNodeElement && !isHandle && connectingNodeId.current) {
            const targetNodeId = targetNodeElement.getAttribute('data-id');

            if (targetNodeId && targetNodeId !== connectingNodeId.current) {
                const label = resolveGatewayLabel(connectingNodeId.current, connectingHandleId.current);
                const animated = connectingHandleId.current !== 'boundary';

                const newEdge = {
                    id: `e-${connectingNodeId.current}-${targetNodeId}-${Date.now()}`,
                    source: connectingNodeId.current,
                    target: targetNodeId,
                    sourceHandle: connectingHandleId.current,
                    targetHandle: null,
                    type: 'deletable' as const,
                    animated,
                    label,
                    style: { stroke: DEFAULT_EDGE_STROKE },
                };
                setEdges((eds) => hasEquivalentEdge(eds, newEdge) ? eds : addEdge(newEdge, eds));
            }
        }

        setNodes((nds) => nds.map((n) => {
            if (!n.data.isConnectableTarget) return n;
            const { isConnectableTarget, ...rest } = n.data;
            return { ...n, data: rest };
        }));

        connectingNodeId.current = null;
        connectingHandleId.current = null;
        connectionCompleted.current = false;
    }, [setNodes, setEdges, resolveGatewayLabel, hasEquivalentEdge]);

    return {
        onConnect,
        onConnectStart,
        onConnectEnd
    };
};
